import { Controller } from "@hotwired/stimulus"

/**
 * SizeTypeSelectorController
 *
 * Manages mutually exclusive size type selection for product line sizes.
 * Displays the relevant input panel based on the selected type and clears
 * all inputs in inactive panels when switching, enforcing mutual exclusivity
 * on the client side before the form is submitted.
 *
 * Size types:
 *   nominal - Nominal Size (sleeving): nominal_size + nominal_size_type
 *   sheet   - Sheet Size (panels):     sheet_dimensions
 *   tubing  - Tubing Size (ID/OD):     inner_diameter + outer_diameter
 *   milspec - MIL-SPEC Size:           mil_spec_size + mil_spec_finish
 *
 * The initial active type is read from `data-initial-type` on the controller
 * element, which should be set server-side based on which fields have data.
 *
 * @example
 *   <div data-controller="size-type-selector" data-initial-type="nominal">
 *     <button data-size-type-selector-target="typeButton"
 *             data-type="nominal"
 *             data-action="click->size-type-selector#selectType">...</button>
 *     <div data-size-type-selector-target="nominalPanel">...</div>
 *     <div data-size-type-selector-target="sheetPanel">...</div>
 *     <div data-size-type-selector-target="tubingPanel">...</div>
 *     <div data-size-type-selector-target="milspecPanel">...</div>
 *   </div>
 */
export default class extends Controller {
  static targets = ["typeButton", "nominalPanel", "sheetPanel", "tubingPanel", "milspecPanel"]

  connect() {
    this.activateType(this.element.dataset.initialType || "nominal", false)
  }

  selectType(event) {
    this.activateType(event.currentTarget.dataset.type, true)
  }

  // ============================================================
  // Private
  // ============================================================

  activateType(type, clearInactive) {
    this.#updateButtons(type)
    this.#updatePanels(type, clearInactive)
  }

  #updateButtons(activeType) {
    this.typeButtonTargets.forEach(btn => {
      const isActive = btn.dataset.type === activeType
      btn.classList.toggle("list-group-item-primary", isActive)
      btn.classList.toggle("border-start", isActive)
      btn.classList.toggle("border-primary", isActive)
      btn.classList.toggle("border-3", isActive)
    })
  }

  #updatePanels(activeType, clearInactive) {
    const panelMap = {
      nominal: this.nominalPanelTarget,
      sheet: this.sheetPanelTarget,
      tubing: this.tubingPanelTarget,
      milspec: this.milspecPanelTarget,
    }

    Object.entries(panelMap).forEach(([panelType, panel]) => {
      const isActive = panelType === activeType
      panel.classList.toggle("d-none", !isActive)

      if (!isActive && clearInactive) {
        this.#clearPanelInputs(panel)
      }
    })
  }

  #clearPanelInputs(panel) {
    panel.querySelectorAll("input[type='text'], input[type='hidden']").forEach(input => {
      input.value = ""
    })
    panel.querySelectorAll("select").forEach(select => {
      if (select.options.length > 0) select.selectedIndex = 0
    })
  }
}
